// frontend/src/components/briefing/briefing-model.ts
//
// Pure helpers for the Command Briefing: who speaks, what the quiet line says,
// what the five tiles and the table show, and how a typed instruction is read.
//
// Rule 10: nothing here decides anything about a procurement. The server sends
// the pending gates, the stage counts and the figures; these functions pick
// the item to lead with, turn data into words and drop any clause that has no
// fact behind it. Import-free apart from types and the roster, so Node's test
// runner runs them (`npm run test:unit`).

import { OPEN_LABEL_BY_ANSWER } from './briefing-cards'
import {
  RECORD_ACTS,
  TENDER_ACTS,
  recordActForGate,
  recordActForStage,
  type Lifecycle,
} from '../tender/workbench-config'
import {
  coworkerByName,
  coworkerForSkill,
  coworkerForSkillFive,
  coworkerForStage,
  coworkerForStageFive,
  resolveCoworker,
  type Coworker,
  type CoworkerKey,
} from '../../lib/coworkers'
import type {
  AgentHubValue,
  PendingApproval,
  SupplyDesk,
} from '../../types/contracts'
import type { AskAnswerMatched, FmLinkTarget } from '../../lib/fm-contracts'
import { demoNow } from '../../lib/demo-clock'
import { baselineSource, baselineWithdrawn } from '../tender/list/procurement-model'
import {
  ANSWER_ACTION_COPY,
  CLAIM_COPY,
  NEEDS_COPY,
  NEEDS_COPY_FIVE,
  STEP_WORDS,
  STEP_WORDS_FIVE,
} from './briefing-copy'

// ---------------------------------------------------------------------------
// Payload shapes the briefing reads
// ---------------------------------------------------------------------------

/** One gate on a tender row, as the pipeline endpoint sends it. */
export interface PipelineGate {
  status?: string | null
  requested_at?: string | null
  raised_by?: string | null
}

/** One tender row from GET /api/tenders/pipeline. Optional fields are read when the server sends them. */
export interface PipelineRow {
  tender_ref: string
  title: string
  short_title?: string | null
  stage: number
  created_at?: string | null
  estimated_value?: number | null
  currency?: string | null
  regime?: string | null
  route_type?: string | null
  gate_states?: Record<string, PipelineGate> | null
  /** The stage the row reads on six stages (discover ... manage), sent by the server. */
  act_key?: string | null
  /** The pending gate the row waits on, sent by the server. */
  needs_you_gate?: string | null
  /** True on the row the briefing leads with. */
  lead?: boolean | null
}

export interface PipelineSnapshot {
  rows: PipelineRow[]
  /** summary.total: the live procurements in scope, as the rail badge reads it. */
  total: number
  /** summary.by_stage: backend stage number to count. */
  byStage: Record<string, number>
}

/**
 * Optional server block for the briefing (plan 02 D2, D8, D11). When the
 * server sends it, it wins: the quiet line is Settings copy filled on the
 * server, and "Coming up" reads contracts inside the renewal window. Without
 * it the page composes from the pending gates and the pipeline.
 */
export interface ServerBriefing {
  /** The server's demo "now" (ISO, with a zone). */
  now?: string | null
  lead?: {
    tender_ref: string
    /** Coworker key or first name. */
    speaker: string
    /** The sentence after the speaker's name. */
    quiet_line: string
    link_label?: string | null
    href?: string | null
    gate_id?: string | null
  } | null
  coming_up?: Array<{
    tender_ref: string
    title: string
    /** "Contract ends 30 June 2027". */
    sub?: string | null
    /** "PSR renewal, 9 months". */
    tile_sub?: string | null
    regime?: string | null
    route_label?: string | null
    coworker?: string | null
    href?: string | null
  }> | null
  delivery?: {
    title: string
    sub?: string | null
    href?: string | null
    coworker?: string | null
  } | null
  answered_since_yesterday?: number | null
  /** Contracts inside the renewal window: PA's "contracts approaching renewal". */
  renewals?: {
    count: number
    window_months?: number | null
    items?: Array<{
      contract: string
      label: string
      /** ISO date, "2027-01-31". */
      end_date: string
      tender_ref?: string | null
    }>
  } | null
  /** What is behind a decision, by procurement: "5 findings and 1 policy check to look at". */
  context_lines?: Record<string, string> | null
  // ---- Plan 02 section 7.2: screen one, built on the server ----------------
  /** "Good morning", from the demo clock. */
  greeting?: string | null
  /** "MONDAY 12 OCTOBER". */
  date_line?: string | null
  megan?: MeganLine | null
  cards?: BriefingCardModel[] | null
  coworkers_today?: CoworkersTodayModel | null
  more_approvals?: MoreApprovalsModel | null
  answers?: BriefingAnswers | null
  /** How soon to ask again: 3 s while a card works or has just completed. */
  poll_ms?: number | null
}

export interface MeganLine {
  state: string
  line: string
  /** Megan's ping ring: something waits on the person. */
  active: boolean
}

export type BriefingCardStatus = 'needs_you' | 'working' | 'completed'

export interface BriefingCardModel {
  id: 'fm' | 'contract_action' | 'desk'
  case_ref: string
  state: string
  status: BriefingCardStatus
  kicker: string
  title: string
  body: string
  coworker: string
  pill: string
  /** "Your decision: ..." on a needs-you card; empty otherwise (plan 11 A.1.6). */
  ask?: string
  status_label: string
  href: string
  gate_id?: string | null
  changed_at?: string | null
}

export interface CoworkersTodayModel {
  faces: Array<{ key: string; status: 'working' | 'waiting' | 'done' }>
  line: string
}

export interface MoreApprovalsModel {
  count: number
  label: string
  href: string
}

export interface ServerAnswer {
  speaker: string
  steps: string[]
  text: string
  actions: Array<{ label: string; href: string }>
  navigate_to?: string | null
  tiles?: Array<{ label: string; value: string; sub: string }>
}

export interface BriefingAnswers {
  needs?: ServerAnswer | null
  pipeline?: ServerAnswer | null
  today?: ServerAnswer | null
}

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/**
 * The time the hero's date line and greeting read: the demo clock the rest of
 * the briefing measures ages against, not the browser's own clock. Before the
 * server has answered it is the browser clock, as `demoNow` says.
 */
export function briefingNow(): Date {
  return demoNow()
}

/**
 * Questions Sophie answered without the P2P team (`answered_by_sophie`,
 * Supply Chain Desk plan 09).
 */
export function answeredBySophie(
  desk: { answered_by_sophie?: number | null } | null | undefined
): number | null {
  if (!desk) return null
  const value = desk.answered_by_sophie
  return typeof value === 'number' ? value : null
}

/** Good morning before 12:00, afternoon before 18:00, evening after. */
export function greetingFor(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/**
 * The date line and greeting the hero shows. The server's copy wins. Without
 * it, a clock is used only once the demo clock holds the server's time; on the
 * PA build the browser clock is never shown (it says "Saturday 10 October,
 * Good evening" for a moment before the server's Monday morning arrives), so
 * until then both stay blank.
 */
export function heroDateAndGreeting(input: {
  serverDate?: string | null
  serverGreeting?: string | null
  clock: Date | null
  clockSynced: boolean
  paBuild: boolean
}): { dateText: string | null; greeting: string | null } {
  const clock = input.clock && (input.clockSynced || !input.paBuild) ? input.clock : null
  return {
    dateText: input.serverDate ?? (clock ? dateLine(clock).toUpperCase() : null),
    greeting: input.serverGreeting ?? (clock ? greetingFor(clock.getHours()) : null),
  }
}

/** "Monday 12 October", built from parts so it reads the same on every runtime. */
export function dateLine(date: Date): string {
  const weekday = date.toLocaleDateString('en-GB', { weekday: 'long' })
  const month = date.toLocaleDateString('en-GB', { month: 'long' })
  return `${weekday} ${date.getDate()} ${month}`
}

/** "route approval" for a pending approval, from its gate or kind; else the server's label with "and" for "&". */
export function stepWords(
  approval: { gate_id?: string | null; step?: string | null; kind?: string | null },
  lifecycle: Lifecycle = 'six_stage'
): string | null {
  const five = lifecycle === 'five_stage'
  const words = five ? STEP_WORDS_FIVE : STEP_WORDS
  if (!five && approval.kind === 'contract_action') return STEP_WORDS.contract_action
  if (approval.gate_id && words[approval.gate_id]) return words[approval.gate_id]
  return approval.step ? approval.step.replace(/\s*&\s*/g, ' and ').toLowerCase() : null
}

/** The Explorer's short title when the server sends one, else the title up to its first comma or dash. */
/** The name of a procurement in a table row or a sentence: "Hospital beds", not its whole short title. */
export function briefingItem(row: { title?: string | null; short_title?: string | null }): string {
  const name = shortTitle(row)
  return name.split(',')[0]?.trim() || name
}

export function shortTitle(row: { title?: string | null; short_title?: string | null }): string {
  const given = row.short_title?.trim()
  if (given) return given
  const title = (row.title ?? '').trim()
  const cut = title.split(/,| - | \u2013 /)[0]?.trim()
  return cut || title
}

const NUMBER_WORDS = ['no', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten']

/** "Two" for 2, the digits above ten. Used at the start of a sentence. */
export function countWord(n: number): string {
  return n >= 1 && n <= 10 ? NUMBER_WORDS[n] : String(n)
}

/**
 * "2 h", "3 days", "40 min". Null when there is no usable timestamp. Hours
 * round to the nearest hour, as the Tender Explorer draws them (v2-s02), so a
 * wait reads the same in the briefing and in the lists.
 */
export function waitingLabel(requestedAt: string | null | undefined, now: Date): string | null {
  if (!requestedAt) return null
  const then = new Date(requestedAt).getTime()
  if (Number.isNaN(then)) return null
  const minutes = Math.floor((now.getTime() - then) / 60_000)
  if (minutes < 0) return null
  if (minutes < 60) return minutes < 1 ? 'just now' : `${minutes} min`
  const hours = Math.round(minutes / 60)
  if (minutes < 24 * 60) return hours >= 24 ? '1 day' : `${hours} h`
  const days = Math.floor(minutes / (24 * 60))
  return days === 1 ? '1 day' : `${days} days`
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-GB').format(n)
}

// ---------------------------------------------------------------------------
// Stages and coworkers
// ---------------------------------------------------------------------------

/**
 * A stage key the briefing counts under. The six-stage keys are discover,
 * define, source, evaluate, negotiate and manage; `procure` and `award` are the
 * five-stage names, kept for the five-stage helpers.
 */
export type ActKey =
  | 'discover'
  | 'define'
  | 'source'
  | 'procure'
  | 'evaluate'
  | 'negotiate'
  | 'award'
  | 'manage'

export interface ActCount {
  key: ActKey
  label: string
  count: number
}

/** The six acts in order, with the count of tenders in each. Backend stage labels never leave this file. */
export function actCounts(byStage: Record<string, number> | null | undefined): ActCount[] {
  return RECORD_ACTS.map((act) => ({
    key: act.key,
    label: act.title,
    count: act.stages.reduce((sum, stage) => sum + (byStage?.[String(stage)] ?? 0), 0),
  }))
}

/** The five acts in order, with the count of tenders in each. The five-stage twin of actCounts. */
export function actCountsFive(byStage: Record<string, number> | null | undefined): ActCount[] {
  return TENDER_ACTS.map((act) => ({
    key: act.id === 'market' ? 'procure' : act.id,
    label: act.title,
    count: act.stages.reduce((sum, stage) => sum + (byStage?.[String(stage)] ?? 0), 0),
  }))
}

/**
 * The six acts counted from the stage each row reads (the pipeline's `act_key`),
 * which is how the legacy read rule reaches the count: beds sits at backend
 * stage 1 but reads Source. A key the six acts do not know is not counted.
 */
export function actCountsFromKeys(keys: ReadonlyArray<string | null | undefined>): ActCount[] {
  return RECORD_ACTS.map((act) => ({
    key: act.key,
    label: act.title,
    count: keys.filter((k) => k === act.key).length,
  }))
}

/** The act key a backend stage number belongs to. */
export function actKeyForStage(stage: number): ActKey {
  return recordActForStage(stage).key
}

/** The five-stage twin of actKeyForStage. */
export function actKeyForStageFive(stage: number): ActKey {
  const act = TENDER_ACTS.find((a) => a.stages.includes(stage)) ?? TENDER_ACTS[0]
  return act.id === 'market' ? 'procure' : act.id
}

/** The act a gate belongs to: GD Discover, GR Define, G1 and G2 Source, G3 Evaluate, G4 Negotiate, G5 Manage. */
export function actKeyForGate(gateId: string | null | undefined): ActKey | undefined {
  return recordActForGate(gateId)?.key
}

/** The act whose closing gate is the given gate id ("G1" is Define). The five-stage twin of actKeyForGate. */
export function actKeyForGateFive(gateId: string | null | undefined): ActKey | undefined {
  const act = TENDER_ACTS.find((a) => a.gate === gateId)
  if (!act) return undefined
  return act.id === 'market' ? 'procure' : act.id
}

/**
 * The coworker who raised an approval: the server's name or skill id first,
 * then the stage the gate belongs to (six stages: GD Hannah, GR Ruby, G1 and
 * G2 Owen, G3 Grace, G4 Sam, G5 Leo). A decision on a contract action is Leo's.
 */
export function coworkerForApproval(
  approval: Pick<PendingApproval, 'raised_by' | 'gate_id'> & Partial<Pick<PendingApproval, 'act_key' | 'kind'>>,
  lifecycle: Lifecycle = 'six_stage'
): Coworker | undefined {
  const five = lifecycle === 'five_stage'
  const raised = approval.raised_by?.trim()
  if (raised) {
    const bySkill = five ? coworkerForSkillFive(raised) : coworkerForSkill(raised)
    const byName = coworkerByName(raised) ?? resolveCoworker(raised) ?? bySkill
    if (byName) return byName
  }
  if (five) {
    const key = actKeyForGateFive(approval.gate_id)
    return key ? coworkerForStageFive(key) : undefined
  }
  if (approval.kind === 'contract_action') return coworkerForStage('manage')
  const key = approval.act_key?.trim() || actKeyForGate(approval.gate_id)
  return key ? coworkerForStage(key) : undefined
}

// ---------------------------------------------------------------------------
// Lead item
// ---------------------------------------------------------------------------

const DAY_MS = 24 * 60 * 60 * 1000
const NEW_WINDOW_MS = 2 * 60 * 60 * 1000

export interface BriefingLead {
  tenderRef: string
  title: string
  item: string
  gateId: string | null
  /** `contract_action` for Leo's decision on a live contract. */
  kind: 'gate' | 'contract_action'
  /** Where the decision opens: the record, at the decision. */
  href: string
  stepLabel: string | null
  coworker: Coworker | undefined
  requestedAt: string | null
  /** A tender created in the last 24 hours that is waiting on a decision. */
  freshlyOpened: boolean
  createdAt: string | null
}

function createdAtFor(ref: string, rows: PipelineRow[]): string | null {
  return rows.find((row) => row.tender_ref === ref)?.created_at ?? null
}

function requestedMs(approval: PendingApproval): number {
  const t = approval.requested_at ? new Date(approval.requested_at).getTime() : NaN
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

/**
 * The item the quiet line leads with. Six stages: among the pending approvals on
 * tenders opened in the last 24 hours, the tender opened first leads (the
 * request time resets at every gate and the opening time does not, so the line
 * tells one story through the walk). Five stages (plan 02 D2): the oldest
 * request. With nothing fresh, the oldest pending approval. Null when nothing is
 * waiting. This is selection, not a business rule; the server may override it
 * with `briefing.lead`.
 */
export function pickLead(
  approvals: PendingApproval[],
  rows: PipelineRow[],
  now: Date,
  lifecycle: Lifecycle = 'six_stage'
): BriefingLead | null {
  if (approvals.length === 0) return null
  // A contract action is Leo's decision on a live contract: it leads only when no gate waits.
  const gates = approvals.filter((a) => a.kind !== 'contract_action')
  const pool = gates.length > 0 ? gates : approvals

  const withAge = pool.map((approval) => {
    const createdAt = createdAtFor(approval.tender_ref, rows)
    const created = createdAt ? new Date(createdAt).getTime() : NaN
    const fresh = !Number.isNaN(created) && now.getTime() - created <= DAY_MS && now.getTime() >= created
    return { approval, createdAt, fresh, created }
  })

  const byOldest = (a: { approval: PendingApproval }, b: { approval: PendingApproval }) =>
    requestedMs(a.approval) - requestedMs(b.approval)
  const byOpened = (
    a: { approval: PendingApproval; created: number },
    b: { approval: PendingApproval; created: number }
  ) => a.created - b.created || byOldest(a, b)

  const fresh = withAge.filter((entry) => entry.fresh).sort(lifecycle === 'five_stage' ? byOldest : byOpened)
  const chosen = (fresh.length > 0 ? fresh : [...withAge].sort(byOldest))[0]
  const { approval } = chosen
  const row = rows.find((r) => r.tender_ref === approval.tender_ref)

  return {
    tenderRef: approval.tender_ref,
    title: approval.tender_title ?? row?.title ?? approval.tender_ref,
    item: briefingItem({ title: approval.tender_title ?? row?.title, short_title: row?.short_title }),
    gateId: approval.gate_id ?? null,
    kind: approval.kind === 'contract_action' ? 'contract_action' : 'gate',
    href: approval.href?.trim() || decisionHref(approval.tender_ref),
    stepLabel: approval.step ?? null,
    coworker: coworkerForApproval(approval, lifecycle),
    requestedAt: approval.requested_at ?? null,
    freshlyOpened: chosen.fresh,
    createdAt: chosen.createdAt,
  }
}

/** True when a tender was created in the last two hours and this browser has not opened it. */
export function isNewRef(
  createdAt: string | null | undefined,
  ref: string,
  seen: ReadonlySet<string>,
  now: Date
): boolean {
  if (!createdAt || seen.has(ref)) return false
  const created = new Date(createdAt).getTime()
  if (Number.isNaN(created)) return false
  const age = now.getTime() - created
  return age >= 0 && age <= NEW_WINDOW_MS
}

// ---------------------------------------------------------------------------
// The quiet line
// ---------------------------------------------------------------------------

export interface QuietLineModel {
  speaker: Coworker | undefined
  /** The speaker's first name, printed in bold before the colon. */
  speakerName: string
  /** The sentence or two after the colon. */
  text: string
  linkLabel: string
  href: string
  tenderRef: string | null
  gateId: string | null
  /** `contract_action` when the line is about Leo's decision on a live contract. */
  kind?: 'gate' | 'contract_action' | null
}

/** Wording shared by the six-stage and five-stage quiet lines. */
const QUIET_LINE_SHARED = {
  fallback: '{item} needs your decision.',
  nothingWaiting: 'Nothing needs your attention this morning.',
  answeredSince: "I've answered {n} requisitioner questions since yesterday.",
  reviewLabel: 'Review',
  followLabel: 'Follow it',
  seeTodayLabel: 'See today',
} as const

/** The six-stage quiet line templates, keyed by the gate that is waiting. The server's copy still wins. */
export const QUIET_LINE_COPY = {
  byGate: {
    GD: 'The baseline for {item} is ready for you.',
    GR: 'The requirements for {item} are ready for you.',
    G1: 'The route for {item} is ready for you.',
    G2: 'The clarification answers for {item} are ready for you.',
    G3: 'The scores for {item} are ready for your approval.',
    G4: 'The award pack for {item} is ready. Commercial decides this one.',
    G5: 'The review for {item} is ready for you.',
  } as Record<string, string>,
  /** A contractual action Leo has ready on a live contract. */
  contractAction: 'Leo has a contractual action ready on {item}.',
  ...QUIET_LINE_SHARED,
} as const

/** The five-stage quiet line templates, kept beside the six-stage ones. */
export const QUIET_LINE_COPY_FIVE = {
  /** A Define gate on a tender opened in the last 24 hours. */
  defineFresh: 'The business case for {item} was approved this morning. The route is ready for you.',
  /** One line per act, used when the server sends no copy. */
  byAct: {
    define: 'The route for {item} is ready for you.',
    procure: 'The tender pack for {item} is ready for you.',
    evaluate: '{item} is ready for the evaluation lock.',
    award: 'The award pack for {item} is ready. Commercial decides this one.',
    manage: 'The review for {item} is ready for you.',
  },
  ...QUIET_LINE_SHARED,
} as const

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '')
}

/** Where "Review" goes: the record, open at the decision. */
export function decisionHref(ref: string): string {
  return `/tender/${encodeURIComponent(ref)}?focus=decision`
}

/**
 * The line under the ask box. The server's copy wins; otherwise the line is
 * built from the lead item, and a clause with no fact behind it is dropped.
 * With nothing waiting it says so (Sophie speaks), and "See today" scrolls to
 * the tiles. Null when there is no data at all, so the hero shows the
 * greeting and the box alone.
 */
export function buildQuietLine(input: {
  server?: ServerBriefing | null
  lead: BriefingLead | null
  hasData: boolean
  /** Six stages unless this says `five_stage`. */
  lifecycle?: Lifecycle
}): QuietLineModel | null {
  const { server, lead, hasData } = input
  const five = input.lifecycle === 'five_stage'

  const serverLead = server?.lead
  if (serverLead?.quiet_line) {
    const speaker = resolveCoworker(serverLead.speaker)
    return {
      speaker,
      speakerName: speaker?.name ?? serverLead.speaker,
      text: serverLead.quiet_line,
      linkLabel:
        serverLead.link_label ||
        (!five && serverLead.gate_id === 'G4' ? QUIET_LINE_COPY.followLabel : QUIET_LINE_COPY.reviewLabel),
      href: serverLead.href || decisionHref(serverLead.tender_ref),
      tenderRef: serverLead.tender_ref,
      gateId: serverLead.gate_id ?? null,
    }
  }

  if (lead && five) {
    const act = actKeyForGateFive(lead.gateId)
    const template =
      act === 'define' && lead.freshlyOpened
        ? QUIET_LINE_COPY_FIVE.defineFresh
        : act && act in QUIET_LINE_COPY_FIVE.byAct
          ? QUIET_LINE_COPY_FIVE.byAct[act as keyof typeof QUIET_LINE_COPY_FIVE.byAct]
          : QUIET_LINE_COPY_FIVE.fallback
    const speaker = lead.coworker
    return {
      speaker,
      speakerName: speaker?.name ?? 'Your coworker',
      text: fill(template, { item: lead.item }),
      linkLabel: act === 'award' ? QUIET_LINE_COPY_FIVE.followLabel : QUIET_LINE_COPY_FIVE.reviewLabel,
      href: decisionHref(lead.tenderRef),
      tenderRef: lead.tenderRef,
      gateId: lead.gateId,
    }
  }

  if (lead) {
    const gate = lead.gateId ?? ''
    const template =
      lead.kind === 'contract_action'
        ? QUIET_LINE_COPY.contractAction
        : (QUIET_LINE_COPY.byGate[gate] ?? QUIET_LINE_COPY.fallback)
    const speaker = lead.coworker
    return {
      speaker,
      speakerName: speaker?.name ?? 'Your coworker',
      text: fill(template, { item: lead.item }),
      linkLabel: gate === 'G4' ? QUIET_LINE_COPY.followLabel : QUIET_LINE_COPY.reviewLabel,
      href: lead.kind === 'contract_action' ? lead.href : decisionHref(lead.tenderRef),
      tenderRef: lead.tenderRef,
      gateId: lead.gateId,
      kind: lead.kind,
    }
  }

  if (!hasData) return null

  const answered = server?.answered_since_yesterday
  const sophie = resolveCoworker('sophie')
  const second =
    typeof answered === 'number' && answered > 0
      ? ` ${fill(QUIET_LINE_COPY.answeredSince, { n: formatNumber(answered) })}`
      : ''
  return {
    speaker: sophie,
    speakerName: sophie?.name ?? 'Sophie',
    text: `${QUIET_LINE_COPY.nothingWaiting}${second}`,
    linkLabel: QUIET_LINE_COPY.seeTodayLabel,
    href: '#today',
    tenderRef: null,
    gateId: null,
  }
}

// ---------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------

export interface TileModel {
  /** `coming` and `saved` are the five-stage tiles; `renewals` and `hours` replace them on six stages. */
  key: 'live' | 'waiting' | 'coming' | 'renewals' | 'questions' | 'saved' | 'hours'
  label: string
  value: string
  sub: string | null
  tone: 'neutral' | 'amber' | 'green'
  href: string
  /** Name on the link, built from label and value. */
  ariaLabel: string
}

export interface TileInput {
  pipeline: PipelineSnapshot | null
  approvals: PendingApproval[] | null
  server: ServerBriefing | null
  desk: SupplyDesk | null
  value: AgentHubValue | null
  now: Date
}

function positive(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0
}

/** The oldest wait among the pending approvals, in words, or null. */
export function oldestWait(approvals: PendingApproval[], now: Date): string | null {
  const oldest = [...approvals].sort((a, b) => requestedMs(a) - requestedMs(b))[0]
  return oldest ? waitingLabel(oldest.requested_at, now) : null
}

/**
 * How many stages the live procurements are spread over. On six stages the
 * count reads each row's own stage (`act_key`, which carries the legacy read
 * rule: beds sits at backend stage 1 and reads Source); a pipeline that sends
 * none falls back to the backend stage counts.
 */
function stagesInUse(pipeline: PipelineSnapshot, lifecycle: Lifecycle): number {
  if (lifecycle === 'five_stage') return actCountsFive(pipeline.byStage).filter((a) => a.count > 0).length
  return actCountsFor(pipeline).filter((a) => a.count > 0).length
}

/** The six acts with their counts, from each row's `act_key` when the server sends it, else the backend stages. */
export function actCountsFor(pipeline: Pick<PipelineSnapshot, 'rows' | 'byStage'>): ActCount[] {
  const keys = pipeline.rows.map((row) => row.act_key)
  if (pipeline.rows.length > 0 && keys.every((k) => typeof k === 'string' && k)) return actCountsFromKeys(keys)
  return actCounts(pipeline.byStage)
}

/** "6 stages", "1 stage". */
function stagesPhrase(n: number): string {
  return n === 1 ? '1 stage' : `${n} stages`
}

/**
 * The whole hours a value payload says were given back this year, or null. A
 * withdrawn baseline hides it, whether the sign-off flag or the baseline block
 * says so (a block with no words counts as withdrawn).
 */
export function staffHours(value: AgentHubValue | null | undefined): number | null {
  if (!value || value.baseline_signed_off === false || baselineWithdrawn(value)) return null
  return positive(value.total_hours_saved) ? Math.round(value.total_hours_saved) : null
}

/** "against an agreed baseline", from the value payload's own words. */
export function baselineWords(value: AgentHubValue | null | undefined): string {
  return value?.baseline?.short?.trim() || CLAIM_COPY.baselineFallback
}

/**
 * The tiles, each only when its figure exists (no zeros on screen). Six stages:
 * live, waiting, contracts to renew, staff hours given back, then Sophie's
 * questions last (she is off the main path). Five stages: live, waiting, coming
 * up, questions, time saved.
 */
export function buildTiles(input: TileInput, lifecycle: Lifecycle = 'six_stage'): TileModel[] {
  const { pipeline, approvals, server, desk, value, now } = input
  const five = lifecycle === 'five_stage'
  const tiles: TileModel[] = []

  if (pipeline && positive(pipeline.total)) {
    const acts = stagesInUse(pipeline, lifecycle)
    const sub = acts > 0 ? `across ${stagesPhrase(acts)}` : null
    tiles.push({
      key: 'live',
      label: 'Live procurements',
      value: formatNumber(pipeline.total),
      sub,
      tone: 'neutral',
      href: '/tender',
      ariaLabel: `Live procurements: ${pipeline.total}${sub ? `, ${sub}` : ''}`,
    })
  }

  if (approvals && approvals.length > 0) {
    const oldest = oldestWait(approvals, now)
    const sub = oldest ? `oldest ${oldest}` : null
    tiles.push({
      key: 'waiting',
      label: 'Waiting on you',
      value: String(approvals.length),
      sub,
      tone: 'amber',
      href: '/procurement/approvals',
      ariaLabel: `Waiting on you: ${approvals.length}${sub ? `, ${sub}` : ''}`,
    })
  }

  if (five) {
    const coming = server?.coming_up ?? []
    if (coming.length > 0) {
      const first = coming[0]
      tiles.push({
        key: 'coming',
        label: 'Coming up',
        value: String(coming.length),
        sub: first.tile_sub ?? null,
        tone: 'neutral',
        href: first.href || `/tender/${encodeURIComponent(first.tender_ref)}`,
        ariaLabel: `Coming up: ${coming.length}${first.tile_sub ? `, ${first.tile_sub}` : ''}`,
      })
    }
  } else if (server?.renewals && positive(server.renewals.count)) {
    const { count, window_months: months } = server.renewals
    const sub = positive(months) ? `end within ${months} months` : null
    tiles.push({
      key: 'renewals',
      label: 'Contracts to renew',
      value: String(count),
      sub,
      tone: 'neutral',
      href: '/contracts?view=renewals',
      ariaLabel: `Contracts to renew: ${count}${sub ? `, ${sub}` : ''}`,
    })
  }

  if (!five) {
    const hours = staffHours(value)
    if (hours !== null) {
      const sub = `this year, ${baselineWords(value)}`
      tiles.push({
        key: 'hours',
        label: 'Staff hours given back',
        value: `${formatNumber(hours)} h`,
        sub,
        tone: 'green',
        href: '/agent-hub?tab=value',
        ariaLabel: `Staff hours given back: ${formatNumber(hours)} hours, ${sub}`,
      })
    }
  }

  if (desk && positive(desk.questions_this_month)) {
    const count = answeredBySophie(desk)
    const answered = positive(count) ? `${formatNumber(count)} answered by Sophie` : null
    tiles.push({
      key: 'questions',
      label: 'Requisitioner questions',
      value: formatNumber(desk.questions_this_month),
      sub: answered,
      tone: 'neutral',
      href: '/supply-chain',
      ariaLabel: `Requisitioner questions: ${desk.questions_this_month}${answered ? `, ${answered}` : ''}`,
    })
  }

  if (five && value && positive(value.working_days_saved)) {
    const days = Math.round(value.working_days_saved)
    if (days > 0) {
      tiles.push({
        key: 'saved',
        label: 'Time saved',
        value: `${days} ${days === 1 ? 'day' : 'days'}`,
        sub: 'working days, this month',
        tone: 'green',
        href: '/agent-hub?tab=value',
        ariaLabel: `Time saved: ${days} working ${days === 1 ? 'day' : 'days'} this month`,
      })
    }
  }

  return tiles
}

// ---------------------------------------------------------------------------
// The "What needs you, and what is next" table
// ---------------------------------------------------------------------------

export interface TableRowModel {
  key: string
  coworkerKey: CoworkerKey | null
  coworkerName: string
  item: string
  sub: string | null
  status: string
  statusTone: 'amber' | 'blue' | 'grey'
  waiting: string | null
  /** A function ("Procurement") or a regime pill. */
  decides: { kind: 'text'; text: string } | { kind: 'regime'; regime: string; route?: string | null; label?: string | null } | null
  href: string
  needsYou: boolean
}

export const TABLE_MAX_ROWS = 5
/** Decisions are never cut while this many or fewer wait; the fifth slot goes to what is next. */
export const TABLE_MAX_DECISIONS = 4

export interface TableInput {
  approvals: PendingApproval[] | null
  rows: PipelineRow[]
  server: ServerBriefing | null
  desk: SupplyDesk | null
  leadRef: string | null
  now: Date
  /** Six stages unless this says `five_stage`. */
  lifecycle?: Lifecycle
}

function approvalRow(
  approval: PendingApproval,
  rows: PipelineRow[],
  now: Date,
  context: string | null,
  lifecycle: Lifecycle
): TableRowModel {
  const row = rows.find((r) => r.tender_ref === approval.tender_ref)
  const worker = coworkerForApproval(approval, lifecycle)
  const step = stepWords(approval, lifecycle)
  const item = briefingItem({ title: approval.tender_title ?? row?.title, short_title: row?.short_title })
  return {
    key: `${approval.tender_ref}-${approval.gate_id ?? approval.action_code ?? approval.step ?? 'gate'}`,
    coworkerKey: worker?.key ?? null,
    coworkerName: worker?.name ?? 'Your coworker',
    item: step ? `${item} · ${step}` : item,
    sub: context,
    status: 'Needs you',
    statusTone: 'amber',
    waiting: waitingLabel(approval.requested_at, now),
    decides: approval.owner_function ? { kind: 'text', text: approval.owner_function } : null,
    href: approval.href?.trim() || decisionHref(approval.tender_ref),
    needsYou: true,
  }
}

/**
 * At most five rows: what needs you (the lead first, then the oldest), what
 * is coming up, and Sophie's rows. A row appears only when its data does.
 * Six stages: decisions are never cut while four or fewer wait, and the fifth
 * slot goes to the contract that is next, then Sophie's handover, then her
 * delivery. Five stages: trailing rows keep their room and decisions give way.
 * If more decisions wait than fit, the caller adds "See all in My Approvals".
 */
export function buildTableRows(input: TableInput): { rows: TableRowModel[]; moreApprovals: boolean } {
  const { approvals, rows: pipelineRows, server, desk, leadRef, now } = input
  const lifecycle = input.lifecycle ?? 'six_stage'
  const out: TableRowModel[] = []

  const sorted = [...(approvals ?? [])].sort((a, b) => {
    if (leadRef) {
      if (a.tender_ref === leadRef && b.tender_ref !== leadRef) return -1
      if (b.tender_ref === leadRef && a.tender_ref !== leadRef) return 1
    }
    return requestedMs(a) - requestedMs(b)
  })

  const comingUp = server?.coming_up?.[0]
  const handedOver = desk && positive(desk.handover_open) ? desk.handover_open : null
  const delivery = server?.delivery ?? null
  const atRisk = desk && positive(desk.at_risk) ? desk.at_risk : null
  const trailingCount = (comingUp ? 1 : 0) + (handedOver ? 1 : 0) + (delivery || atRisk ? 1 : 0)

  // Keep room for the rows that follow (five stages), or never cut a decision
  // while four or fewer wait (six stages).
  const roomForApprovals =
    lifecycle === 'five_stage'
      ? Math.max(1, TABLE_MAX_ROWS - trailingCount)
      : Math.min(sorted.length, TABLE_MAX_DECISIONS)
  const context = server?.context_lines ?? {}

  for (const approval of sorted.slice(0, roomForApprovals)) {
    out.push(approvalRow(approval, pipelineRows, now, context[approval.tender_ref] ?? null, lifecycle))
  }

  const trailing: TableRowModel[] = []

  if (comingUp) {
    const worker = resolveCoworker(comingUp.coworker ?? 'hannah')
    trailing.push({
      key: `coming-${comingUp.tender_ref}`,
      coworkerKey: worker?.key ?? null,
      coworkerName: worker?.name ?? 'Hannah',
      item: shortTitle({ title: comingUp.title }),
      sub: comingUp.sub ?? null,
      status: 'Coming up',
      statusTone: 'blue',
      waiting: null,
      decides: comingUp.regime
        ? { kind: 'regime', regime: comingUp.regime, label: comingUp.route_label ?? null }
        : null,
      href: comingUp.href || `/tender/${encodeURIComponent(comingUp.tender_ref)}`,
      needsYou: false,
    })
  }

  if (handedOver) {
    const sophie = resolveCoworker('sophie')
    trailing.push({
      key: 'sophie-p2p',
      coworkerKey: sophie?.key ?? null,
      coworkerName: sophie?.name ?? 'Sophie',
      item: `${handedOver} ${handedOver === 1 ? 'question' : 'questions'} handed to P2P`,
      sub: null,
      status: 'With P2P',
      statusTone: 'amber',
      waiting: 'today',
      decides: { kind: 'text', text: 'P2P team' },
      href: '/supply-chain',
      needsYou: false,
    })
  }

  if (delivery) {
    const sophie = resolveCoworker(delivery.coworker ?? 'sophie')
    trailing.push({
      key: 'sophie-delivery',
      coworkerKey: sophie?.key ?? null,
      coworkerName: sophie?.name ?? 'Sophie',
      item: delivery.title,
      sub: delivery.sub ?? null,
      status: 'Handled',
      statusTone: 'blue',
      waiting: null,
      decides: null,
      href: delivery.href || '/supply-chain/orders',
      needsYou: false,
    })
  } else if (atRisk) {
    const sophie = resolveCoworker('sophie')
    const needsPerson = desk && positive(desk.needs_person) ? desk.needs_person : null
    trailing.push({
      key: 'sophie-at-risk',
      coworkerKey: sophie?.key ?? null,
      coworkerName: sophie?.name ?? 'Sophie',
      item: `${atRisk} ${atRisk === 1 ? 'delivery' : 'deliveries'} at risk`,
      sub: needsPerson ? `${needsPerson} needs a person` : null,
      status: 'Sophie is on it',
      statusTone: 'blue',
      waiting: null,
      decides: null,
      href: '/supply-chain/orders',
      needsYou: false,
    })
  }

  out.push(...trailing)

  return {
    rows: out.slice(0, TABLE_MAX_ROWS),
    moreApprovals: (approvals?.length ?? 0) > roomForApprovals,
  }
}

// ---------------------------------------------------------------------------
// Hovers built from live figures (static copy lives in lib/hover-copy/briefing.ts)
// ---------------------------------------------------------------------------

export interface DynamicHover {
  title: string
  body: string
  source: string
}

/** "Discover 1 · Define 1 · Source 4 ...", skipping empty acts. Pass `five_stage` for "Define 3 · Procure 2 ...". */
export function liveByStageHover(
  byStage: Record<string, number> | null | undefined,
  lifecycle: Lifecycle = 'six_stage'
): DynamicHover | null {
  const parts = (lifecycle === 'five_stage' ? actCountsFive : actCounts)(byStage).filter((a) => a.count > 0)
  if (parts.length === 0) return null
  return {
    title: 'Live procurements by stage',
    body: parts.map((a) => `${a.label} ${a.count}`).join(' · '),
    source: 'Source: procurement pipeline',
  }
}

/** The by-hand baselines behind the time saved figure, from the value payload. */
export function timeSavedHover(value: AgentHubValue | null): DynamicHover | null {
  if (!value || !positive(value.working_days_saved) || baselineWithdrawn(value)) return null
  const lines = value.by_act
    .filter((act) => act.by_hand_label)
    .map((act) => `${act.label} ${act.by_hand_label}`)
  const share = value.time_split?.waiting_on_approvals_share
  const sentences = [
    lines.length > 0
      ? `Measured against agreed by-hand times: ${lines.join(', ')}.`
      : 'Measured against agreed by-hand times.',
  ]
  if (typeof share === 'number' && share > 0) {
    sentences.push(`${Math.round(share <= 1 ? share * 100 : share)}% of elapsed time is waiting on approvals.`)
  }
  return {
    title: 'Time saved this month',
    body: sentences.join(' '),
    source: baselineSource(value),
  }
}

/** "Discover 1 · Define 1 · Source 4 ...", counted from the stage each row reads (the server's `act_key`). */
export function liveByStageHoverFor(pipeline: Pick<PipelineSnapshot, 'rows' | 'byStage'> | null): DynamicHover | null {
  if (!pipeline) return null
  const parts = actCountsFor(pipeline).filter((a) => a.count > 0)
  if (parts.length === 0) return null
  return {
    title: 'Live procurements by stage',
    body: parts.map((a) => `${a.label} ${a.count}`).join(' · '),
    source: 'Source: procurement pipeline',
  }
}

/**
 * The by-hand and with-coworkers times behind the staff hours figure, under the
 * baseline's own words. Waiting on people is not counted. Null with no figure.
 */
export function staffHoursHover(value: AgentHubValue | null): DynamicHover | null {
  const hours = staffHours(value)
  if (hours === null || !value) return null
  const lines = value.by_act
    .filter((act) => act.by_hand_label && act.actual_label)
    .map((act) => `${act.label} ${act.by_hand_label} by hand, ${act.actual_label} with coworkers`)
  const base = baselineWords(value)
  const lead = `${base.charAt(0).toUpperCase()}${base.slice(1)}, for each procurement`
  return {
    title: `Staff hours given back this year: ${formatNumber(hours)} h`,
    body: `${lines.length > 0 ? `${lead}: ${lines.join('; ')}.` : `${lead}.`} Waiting on people is not counted.`,
    source: baselineSource(value),
  }
}

/** "31 January 2027" from an ISO date, built from parts so every runtime reads the same. */
function longDate(iso: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return null
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ]
  const month = months[Number(match[2]) - 1]
  return month ? `${Number(match[3])} ${month} ${match[1]}` : null
}

/** Each contract inside the renewal window with its end date, for the "Contracts to renew" tile. */
export function renewalsHover(server: ServerBriefing | null): DynamicHover | null {
  const items = server?.renewals?.items ?? []
  if (items.length === 0) return null
  const lines = items
    .map((item) => {
      const end = longDate(item.end_date)
      return end ? `${item.label}, ${end}` : null
    })
    .filter((line): line is string => Boolean(line))
  if (lines.length === 0) return null
  return {
    title: 'Contracts to renew',
    body: `${lines.join('. ')}.`,
    source: 'Source: contract register',
  }
}

// ---------------------------------------------------------------------------
// The ask box: reading an instruction
// ---------------------------------------------------------------------------

export type Intent =
  | { kind: 'needs' }
  | { kind: 'today' }
  | { kind: 'pipeline' }
  | { kind: 'approve' }
  | { kind: 'open'; ref: string }
  | { kind: 'desk' }
  | { kind: 'orders' }
  | { kind: 'supply-question'; question: string }
  | { kind: 'free'; question: string }

const STOPWORDS = new Set([
  'open', 'show', 'the', 'request', 'tender', 'procurement', 'record', 'please', 'take', 'for',
  'and', 'go', 'see', 'find', 'this', 'that', 'case', 'one', 'me', 'my', 'our', 'with',
])

function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** A tender whose title carries a word the instruction names. One match only; two or more is not a guess. */
export function matchTender(text: string, rows: PipelineRow[]): string | null {
  const refMatch = /\b((?:tnd|psr)-[a-z]+-\d+)\b/i.exec(text)
  if (refMatch) {
    const wanted = refMatch[1].toUpperCase()
    return rows.find((r) => r.tender_ref.toUpperCase() === wanted)?.tender_ref ?? wanted
  }
  const tokens = normalise(text)
    .split(' ')
    .filter((t) => t.length >= 4 && !STOPWORDS.has(t))
  if (tokens.length === 0) return null
  const hits = rows.filter((row) => {
    const hay = ` ${normalise(`${row.title} ${row.short_title ?? ''}`)} `
    return tokens.some((t) => hay.includes(` ${t} `) || hay.includes(` ${t}s `) || hay.includes(` ${t.replace(/s$/, '')} `))
  })
  return hits.length === 1 ? hits[0].tender_ref : null
}

/**
 * What a typed line or a chip asks for. Instructions navigate and never
 * decide: "approve" opens the record at the decision and records nothing
 * (plan 02 5.5). Supply chain questions go to Sophie's desk; everything else
 * goes to the AI Manager chat.
 */
export function readIntent(text: string, rows: PipelineRow[]): Intent {
  const raw = text.trim()
  const t = normalise(raw)

  // Any way of asking to approve opens the record and records nothing: a decision is taken there.
  if (/^(please |can you |could you |i want to |i would like to )?(approve|sign off|agree to|agree|authori[sz]e)\b/.test(t)) {
    return { kind: 'approve' }
  }
  if (/\bwhat (needs|do i need|should i)\b.*\b(me|first|today|to do)\b|\bwhat needs me\b|\bwaiting on me\b|\bneeds? my (approval|decision|attention)\b/.test(t)) {
    return { kind: 'needs' }
  }
  if (/\bhow are we doing\b|\bhow have we done\b|\btime saved\b|\bhow is (today|the day)\b|\b(procurement |my |the )dashboard\b/.test(t)) {
    return { kind: 'today' }
  }
  if (/\b(show|open|see|go to|take me to)\b( me)?( the)? (pipeline|tender explorer|explorer|procurements)\b|^pipeline$/.test(t)) {
    return { kind: 'pipeline' }
  }
  if (/\b(open|show|go to|take me to)\b( me)?( the)? (supply chain desk|desk|supply chain)\b/.test(t)) {
    return { kind: 'desk' }
  }
  if (/\b(open|show|go to|take me to)\b( me)?( the)? (orders|deliveries|orders and deliveries)\b/.test(t)) {
    return { kind: 'orders' }
  }
  if (/\b(open|show|go to|take me to|review)\b/.test(t) || /\b(tnd|psr)-[a-z]+-\d+\b/.test(t)) {
    const ref = matchTender(raw, rows)
    if (ref) return { kind: 'open', ref }
  }
  if (
    /\b(order(ed|s)?|deliver(y|ies|ed)|requisition(s)?|requisitioner|how do i buy|buy|purchase|stock|catalogue|where (is|are) my|commode|oximeters?|invoice)\b/.test(t)
  ) {
    return { kind: 'supply-question', question: raw }
  }
  return { kind: 'free', question: raw }
}

// ---------------------------------------------------------------------------
// Fixed answers (wording fixed, figures read live)
// ---------------------------------------------------------------------------

export interface AnswerAction {
  label: string
  href: string
}

export interface FixedAnswer {
  /** The reading lines shown while the answer is built. */
  steps: string[]
  text: string
  actions: AnswerAction[]
}

/** `{token}` placeholders filled from `values`; a token with no value becomes nothing. */
function fillTokens(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '')
}

/**
 * "What needs me?" Names each waiting decision, who raised it, what is behind it
 * and how long it has waited. Six stages: a sentence per gate (or contract
 * action) from NEEDS_COPY, up to four, with the lead first. Five stages: the
 * three oldest, one sentence for every decision.
 */
export function buildNeedsAnswer(input: {
  approvals: PendingApproval[] | null
  rows: PipelineRow[]
  now: Date
  /** The item the quiet line leads with; it is named first. */
  leadRef?: string | null
  /** What is behind each decision, by procurement ("5 findings and 1 policy check to look at"). */
  contextLines?: Record<string, string> | null
  /** Six stages unless this says `five_stage`. */
  lifecycle?: Lifecycle
}): FixedAnswer {
  const { approvals, rows, now, leadRef } = input
  const lifecycle = input.lifecycle ?? 'six_stage'
  const five = lifecycle === 'five_stage'
  const list = [...(approvals ?? [])].sort((a, b) => {
    if (leadRef) {
      if (a.tender_ref === leadRef && b.tender_ref !== leadRef) return -1
      if (b.tender_ref === leadRef && a.tender_ref !== leadRef) return 1
    }
    return requestedMs(a) - requestedMs(b)
  })
  const steps = ['Reading your approvals queue']

  if (list.length === 0) {
    return {
      steps,
      text: 'Nothing needs your attention this morning.',
      actions: [{ label: 'Open Tender Explorer', href: '/tender' }],
    }
  }

  const head =
    list.length === 1 ? 'One decision is waiting for you.' : `${countWord(list.length)} decisions are waiting for you.`
  const copy = five ? NEEDS_COPY_FIVE : NEEDS_COPY
  const shown = list.slice(0, five ? 3 : TABLE_MAX_DECISIONS)
  const sentences = shown.map((approval) => {
    const row = rows.find((r) => r.tender_ref === approval.tender_ref)
    const name = coworkerForApproval(approval, lifecycle)?.name ?? 'Your coworker'
    const item = briefingItem({ title: approval.tender_title ?? row?.title, short_title: row?.short_title })
    const key = approval.kind === 'contract_action' ? 'contract_action' : (approval.gate_id ?? '')
    const template = copy[key] ?? copy.fallback
    const wait = waitingLabel(approval.requested_at, now)
    const context = input.contextLines?.[approval.tender_ref]
    return fillTokens(template, {
      coworker: name,
      item,
      step: stepWords(approval, lifecycle) ?? 'a decision',
      context: context ? `: ${context}` : '',
      wait: wait ? `, waiting ${wait}` : '',
    })
  })

  const actions: AnswerAction[] = list.slice(0, 2).map((approval) => {
    const row = rows.find((r) => r.tender_ref === approval.tender_ref)
    const item = briefingItem({ title: approval.tender_title ?? row?.title, short_title: row?.short_title })
    return {
      label: `Open ${item.toLowerCase()}`,
      href: approval.kind === 'contract_action' && approval.href ? approval.href : decisionHref(approval.tender_ref),
    }
  })
  actions.push({ label: 'Open My Approvals', href: '/procurement/approvals' })

  return { steps, text: [head, ...sentences].join(' '), actions }
}

/** "1 h 10 min" from minutes; null when not a usable number. */
export function minutesLabel(minutes: unknown): string | null {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes <= 0) return null
  const total = Math.round(minutes)
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} min`
  return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, '0')} min`
}

const SMALL_WORDS = ['no', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1)
}

/** The first word of "September 2026". */
function firstWord(text: unknown): string | null {
  return typeof text === 'string' && text.trim() ? text.trim().split(/\s+/)[0] : null
}

function poundsLabel(amount: unknown): string | null {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) return null
  return `\u00A3${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(amount)}`
}

/** The measure items a six-stage value payload carries, by key. */
function measureOf(value: AgentHubValue | null, key: string) {
  return value?.measures?.items?.find((item) => item.key === key) ?? null
}

/** The clauses that read PA's measures (plan 05 5.5, plan 13 5.3). Each is dropped when its fact is missing. */
function measureClauses(value: AgentHubValue | null): string[] {
  const out: string[] = []

  const spec = measureOf(value, 'specification_time')
  const specTime = minutesLabel(spec?.figures?.actual_minutes)
  const name = resolveCoworker(spec?.coworker ?? 'ruby')?.name
  if (spec && specTime && name && spec.baseline_label?.trim()) {
    out.push(
      fillTokens(CLAIM_COPY.specification, {
        name,
        actual: specTime,
        byHand: lowerFirst(spec.baseline_label.trim()),
      })
    )
  }

  const invoices = measureOf(value, 'invoice_discrepancies')
  const kpis = measureOf(value, 'supplier_performance')
  const count = invoices?.figures?.count
  const amount = poundsLabel(invoices?.figures?.amount)
  const leo = resolveCoworker(invoices?.coworker ?? 'leo')?.name
  const met = kpis?.figures?.met
  const total = kpis?.figures?.total
  const month = firstWord(kpis?.figures?.month)
  const invoicePart =
    typeof count === 'number' && count > 0 && amount && leo
      ? fillTokens(CLAIM_COPY.invoices, {
          name: leo,
          count: String(count),
          noun: count === 1 ? 'discrepancy' : 'discrepancies',
          amount,
        })
      : null
  const kpiTokens =
    typeof met === 'number' && typeof total === 'number' && total > 0 && month
      ? { met: String(met), total: String(total), month }
      : null
  if (invoicePart && kpiTokens) {
    out.push(`${invoicePart}, and ${fillTokens(CLAIM_COPY.kpis, kpiTokens)}.`)
  } else if (invoicePart) {
    out.push(`${invoicePart}.`)
  } else if (kpiTokens) {
    out.push(fillTokens(CLAIM_COPY.kpisAlone, kpiTokens))
  }
  return out
}

/**
 * "How are we doing today?" Six stages, in this order: decisions waiting, live
 * procurements across the stages, the specification time, invoice discrepancies
 * and KPIs, staff hours against the agreed baseline, Sophie last (plan 05 5.5).
 * Five stages: decisions, live, time saved this month, Sophie. A clause with no
 * fact behind it is dropped, so nothing reads zero.
 */
export function buildTodayAnswer(input: {
  approvals: PendingApproval[] | null
  pipeline: PipelineSnapshot | null
  value: AgentHubValue | null
  desk: SupplyDesk | null
  now: Date
  /** Six stages unless this says `five_stage`. */
  lifecycle?: Lifecycle
}): FixedAnswer {
  const { approvals, pipeline, value, desk, now } = input
  const five = input.lifecycle === 'five_stage'
  const steps: string[] = []
  const clauses: string[] = []

  if (approvals) {
    steps.push('Reading your approvals queue')
    if (approvals.length > 0) {
      const oldest = oldestWait(approvals, now)
      clauses.push(
        `${approvals.length === 1 ? 'One decision is' : `${countWord(approvals.length)} decisions are`} waiting for you${oldest ? `, the oldest for ${oldest}` : ''}.`
      )
    } else {
      clauses.push('Nothing is waiting for you.')
    }
  }
  if (pipeline && positive(pipeline.total)) {
    steps.push('Reading the pipeline')
    const stages = five ? 0 : stagesInUse(pipeline, 'six_stage')
    clauses.push(
      stages > 0
        ? fillTokens(CLAIM_COPY.stages, {
            n: formatNumber(pipeline.total),
            stages: SMALL_WORDS[stages] ?? String(stages),
          })
        : `${formatNumber(pipeline.total)} procurements are live.`
    )
  }
  if (five) {
    if (value && positive(value.working_days_saved)) {
      const days = Math.round(value.working_days_saved)
      if (days > 0) {
        steps.push("Reading this month's time saved")
        clauses.push(`The team has saved about ${days} working ${days === 1 ? 'day' : 'days'} this month.`)
      }
    }
  } else {
    const measures = measureClauses(value)
    const hours = staffHours(value)
    if (measures.length > 0 || hours !== null) steps.push(CLAIM_COPY.readingMeasures)
    clauses.push(...measures)
    if (hours !== null) {
      clauses.push(fillTokens(CLAIM_COPY.hours, { hours: formatNumber(hours), baseline: baselineWords(value) }))
    }
  }
  if (desk && positive(desk.questions_this_month) && positive(answeredBySophie(desk))) {
    steps.push("Reading Sophie's desk")
    clauses.push(
      `Sophie has answered ${formatNumber(answeredBySophie(desk) ?? 0)} of ${formatNumber(desk.questions_this_month)} requisitioner questions without your team.`
    )
  }

  if (clauses.length === 0) {
    return {
      steps: ['Reading the pipeline'],
      text: 'I do not have today’s figures yet. Try again in a moment.',
      actions: [{ label: 'Open Tender Explorer', href: '/tender' }],
    }
  }

  return {
    steps,
    text: clauses.join(' '),
    actions: [
      { label: 'Open Coworker Hub', href: '/agent-hub' },
      { label: 'Open Tender Explorer', href: '/tender' },
    ],
  }
}

// ---------------------------------------------------------------------------
// A rehearsed answer (plan 05 section 4.2): the fixed text with links into the record
// ---------------------------------------------------------------------------

/** One stretch of an answer paragraph: plain text, or a link into the record. */
export interface AnswerSegment {
  text: string
  href?: string
}

export interface AnswerParagraphModel {
  segments: AnswerSegment[]
}

/** The URL a link target opens: `${hrefBase}?stage=...&evidence=...`, plus a finding or a tab when present. */
export function targetHref(hrefBase: string, target: FmLinkTarget): string {
  const query: string[] = []
  if (target.stage) query.push(`stage=${encodeURIComponent(target.stage)}`)
  if (target.evidence) query.push(`evidence=${encodeURIComponent(target.evidence)}`)
  if (target.finding) query.push(`finding=${encodeURIComponent(target.finding)}`)
  if (target.tab) query.push(`tab=${encodeURIComponent(target.tab)}`)
  return query.length > 0 ? `${hrefBase}?${query.join('&')}` : hrefBase
}

/**
 * A paragraph cut on its link phrases: the first occurrence of each phrase,
 * case-sensitive, becomes a link. A phrase that is not in the text leaves it as
 * plain text; no warning is raised (the answer's own test guarantees them).
 */
export function linkSegments(
  text: string,
  links: ReadonlyArray<{ phrase: string; target: FmLinkTarget }>,
  hrefBase: string
): AnswerSegment[] {
  const found = links
    .map((link) => ({ link, at: text.indexOf(link.phrase) }))
    .filter((entry) => entry.at >= 0 && entry.link.phrase.length > 0)
    .sort((a, b) => a.at - b.at)
  const segments: AnswerSegment[] = []
  let cursor = 0
  for (const { link, at } of found) {
    if (at < cursor) continue
    if (at > cursor) segments.push({ text: text.slice(cursor, at) })
    segments.push({ text: link.phrase, href: targetHref(hrefBase, link.target) })
    cursor = at + link.phrase.length
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor) })
  return segments.length > 0 ? segments : [{ text }]
}

export interface MatchedAnswerModel {
  kind: 'ready' | 'not_ready' | 'restricted'
  /** The coworker who answers (Grace), for the face and the context line. */
  coworkerKey: string
  /** "Grace · Integrated facilities management · TND-NHS-060". */
  contextLine: string
  /** The reading lines shown while the answer is read. Empty unless the answer is ready. */
  steps: string[]
  paragraphs: AnswerParagraphModel[]
  /** All the text, for the AI Manager drawer's history. */
  text: string
  actions: AnswerAction[]
}

/**
 * What the ask box shows for a matched answer. Ready and allowed: the paragraphs
 * with their links and one action into the comparison. Not ready: the server's
 * line and one action to the record. Restricted: the server's line, no figures
 * and no action. The context line names the procurement because other
 * procurements are in the same stage and the fixed text does not name it.
 */
export function buildMatchedAnswer(answer: AskAnswerMatched, rows: PipelineRow[]): MatchedAnswerModel {
  const row = rows.find((r) => r.tender_ref === answer.tender_ref)
  const title = row ? briefingItem(row) : null
  const contextLine = [answer.coworker.name, title, answer.tender_ref].filter(Boolean).join(' · ')
  const base = answer.href_base

  if (!answer.allowed) {
    const text = answer.text?.trim() ?? ''
    return {
      kind: 'restricted',
      coworkerKey: answer.coworker.key,
      contextLine,
      steps: [],
      paragraphs: [{ segments: [{ text }] }],
      text,
      actions: [],
    }
  }
  if (!answer.ready) {
    const text = answer.text?.trim() ?? ''
    return {
      kind: 'not_ready',
      coworkerKey: answer.coworker.key,
      contextLine,
      steps: [],
      paragraphs: [{ segments: [{ text }] }],
      text,
      actions: [{ label: ANSWER_ACTION_COPY.record, href: `${base}?focus=decision` }],
    }
  }
  // An answer about no record (plan 04 section 8.3: what needs me, the
  // pipeline, the Desk, "not covered") opens only where its action says.
  if (!base) {
    return {
      kind: 'ready',
      coworkerKey: answer.coworker.key,
      contextLine: answer.coworker.name,
      steps: answer.steps,
      paragraphs: answer.paragraphs.map((p) => ({ segments: [{ text: p.text }] })),
      text: answer.paragraphs.map((p) => p.text).join(' '),
      actions: answer.action?.href
        ? [{ label: OPEN_LABEL_BY_ANSWER[answer.answer_id] ?? ANSWER_OPEN_LABEL, href: answer.action.href }]
        : [],
    }
  }
  return {
    kind: 'ready',
    coworkerKey: answer.coworker.key,
    contextLine,
    steps: answer.steps,
    paragraphs: answer.paragraphs.map((p) => ({ segments: linkSegments(p.text, p.links, base) })),
    text: answer.paragraphs.map((p) => p.text).join(' '),
    actions: [
      {
        label: fillTokens(ANSWER_ACTION_COPY.comparison, { name: answer.coworker.name }),
        href: targetHref(base, { stage: 'evaluate', evidence: 'comparison' }),
      },
    ],
  }
}

/** The link under an answer that opens a page (plan 04 section 8.3). */
export const ANSWER_OPEN_LABEL = 'Open it'

/** Megan's line when no rehearsed answer fits (plan 04 section 8.3). */
export const NOT_COVERED_LINE = "I don't have an answer for that one. Try one of these."

/** The supply desk page for a requisitioner question, with the question pre-filled. */
/**
 * The rehearsed desk questions that open their ticket on Sophie's Desk
 * (plan 09 section 5.2). The server holds the same list in
 * GET /api/supply-chain/ask/context `rehearsed`; this is its fallback when
 * the caller has not loaded it.
 */
export const DESK_REHEARSED: ReadonlyArray<{ match: string; ref: string }> = [
  { match: 'where are my pulse oximeters', ref: 'Q-9012' },
  { match: 'how do i buy a bariatric commode', ref: 'Q-9011' },
]

function normaliseDeskQuestion(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9£\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function deskQuestionHref(
  question: string,
  rehearsed: ReadonlyArray<{ match: string; ref: string }> = DESK_REHEARSED
): string {
  const norm = normaliseDeskQuestion(question)
  const hit = rehearsed.find((r) => normaliseDeskQuestion(r.match) === norm)
  if (hit) return `/supply-chain?open=${encodeURIComponent(hit.ref)}`
  return `/supply-chain/ask?q=${encodeURIComponent(question)}`
}

// ---------------------------------------------------------------------------
// Five-stage helpers
// ---------------------------------------------------------------------------
// The briefing as it read before the six-stage lifecycle, kept beside the
// six-stage helpers so a rollback is a one-line switch.

/** `buildQuietLine` with the five-stage templates. */
export function buildQuietLineFive(input: {
  server?: ServerBriefing | null
  lead: BriefingLead | null
  hasData: boolean
}): QuietLineModel | null {
  return buildQuietLine({ ...input, lifecycle: 'five_stage' })
}

/** `buildTiles` counting the five acts. */
export function buildTilesFive(input: TileInput): TileModel[] {
  return buildTiles(input, 'five_stage')
}

/** `liveByStageHover` counting the five acts. */
export function liveByStageHoverFive(
  byStage: Record<string, number> | null | undefined
): DynamicHover | null {
  return liveByStageHover(byStage, 'five_stage')
}

/** `pickLead` with the five-stage roster: a gate with no named coworker reads Hannah on G1, and so on. */
export function pickLeadFive(approvals: PendingApproval[], rows: PipelineRow[], now: Date): BriefingLead | null {
  return pickLead(approvals, rows, now, 'five_stage')
}

/** `buildTableRows` with the five-stage words and room. */
export function buildTableRowsFive(input: Omit<TableInput, 'lifecycle'>): { rows: TableRowModel[]; moreApprovals: boolean } {
  return buildTableRows({ ...input, lifecycle: 'five_stage' })
}

/** `buildNeedsAnswer` with the five-stage sentence. */
export function buildNeedsAnswerFive(input: {
  approvals: PendingApproval[] | null
  rows: PipelineRow[]
  now: Date
  leadRef?: string | null
}): FixedAnswer {
  return buildNeedsAnswer({ ...input, lifecycle: 'five_stage' })
}

/** `buildTodayAnswer` with the five-stage clauses. */
export function buildTodayAnswerFive(input: {
  approvals: PendingApproval[] | null
  pipeline: PipelineSnapshot | null
  value: AgentHubValue | null
  desk: SupplyDesk | null
  now: Date
}): FixedAnswer {
  return buildTodayAnswer({ ...input, lifecycle: 'five_stage' })
}
